import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogIberiaContent, Dialog } from '../../navigation/dialog/Dialog';
import styles from './ModalTravelRequirements.module.css';

export type ModalTravelRequirementsProps = {
	requiredFields: string[];
	defaultOutsider?: boolean;
	isHidden?: boolean;
};

/**
 * Object defining requirement fields groups
 * @constant
 */
const REQUIREMENT_FIELDS = {
	PERSONAL_INFO: ['BIRTH_DATE', 'NATIONALITY', 'HOME_COUNTRY'],
	IDENTIFICATION: ['PSPT', 'DNI'],
	VISA: ['VISA'],
	DESTINATION_ADDRESS: [
		'DESTINATION_ADDRESS',
		'DESTINATION_CITY',
		'DESTINATION_STATE',
		'DESTINATION_ZIP',
		'DESTINATION_COUNTRY'
	]
};

const handleUserInteraction = (setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>) => (type: string) => {
	if (type === 'PRIMARY') {
		setOpenDialog(false);
	}
};

export const ModalTravelRequirements: React.FC<ModalTravelRequirementsProps> = ({
	requiredFields = [],
	defaultOutsider = false,
	isHidden = false
}) => {
	const { t } = useTranslation(['modal-travel-requirements', 'common']);
	const [openDialog, setOpenDialog] = useState(true);

	const requirements = useMemo<string[]>(() => {
		let reqs: string[] = [];
		if (REQUIREMENT_FIELDS.PERSONAL_INFO.some((field) => requiredFields.includes(field))) {
			reqs = [...reqs, t('DATE_OF_BIRTH_NATIONALITY_RESIDENCE')];
		}
		if (REQUIREMENT_FIELDS.IDENTIFICATION.some((field) => requiredFields.includes(field))) {
			reqs = [...reqs, t('IDENTIFICATION_DOCUMENTATION')];
		}
		if (requiredFields.includes(REQUIREMENT_FIELDS.VISA[0])) {
			reqs = [...reqs, t('VALID_VISA_IN_DESTINATION_COUNTRY')];
		}
		if (REQUIREMENT_FIELDS.DESTINATION_ADDRESS.some((field) => requiredFields.includes(field))) {
			reqs = [...reqs, t('ADDRESS_IN_DESTINATION_COUNTRY')];
		}

		return reqs;
	}, [requiredFields, t]);

	useEffect(() => {
		if (requiredFields.length > 0) {
			setOpenDialog(true);
		}
	}, [requiredFields]);

	/**
	 * If there are no required fields, it returns null and the modal will not exit
	 * @returns null
	 */
	if (requirements.length === 0) {
		return null;
	}

	return (
		<Dialog open={openDialog} onOpenChange={setOpenDialog}>
			<DialogIberiaContent
				header={t('TITLE')}
				primaryButton={t('common:CONTINUE')}
				className={styles.modalContent}
				onUserInteraction={handleUserInteraction(setOpenDialog)}
				defaultOutsider={defaultOutsider}
				isHidden={isHidden}
			>
				<section className={styles.infoSection} id="MODAL_TRAVEL_REQUIREMENTS">
					<p>{t('SUBTITLE')}</p>
					<ul>
						{requirements.map((requirement, index) => (
							<li key={index}>{requirement}</li>
						))}
					</ul>
					<p>{t('COMPLETE_ALL_DATA_INFO')}</p>
				</section>
			</DialogIberiaContent>
		</Dialog>
	);
};
